import { useState } from "react";
import { type Agent, api } from "../lib/api.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Button, InfoBanner, Input, Page, Select, Table } from "../ui/index.ts";

const STATUSES = [
	{ value: "active", label: "active" },
	{ value: "paused", label: "paused" },
	{ value: "retired", label: "retired" },
];

export function Agents() {
	const [agents, reload] = useLoad(() => api.agents());
	const [name, setName] = useState("");
	const [kind, setKind] = useState("buyer");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function run(action: () => Promise<unknown>) {
		setBusy(true);
		setError(null);
		try {
			await action();
			reload();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	async function create(e: React.FormEvent) {
		e.preventDefault();
		if (!name.trim()) return;
		setBusy(true);
		setError(null);
		try {
			await api.createAgent({ name: name.trim(), kind });
			setName("");
			reload();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Page
			title="Agents"
			description="The buying agents that act on the ad accounts. Access to each one is handed out on the Teams and Access pages."
		>
			{error && <InfoBanner variant="danger" title={error} />}
			{agents.state === "ready" && agents.data.truncated && (
				<InfoBanner variant="warning" title="This list is not complete.">
					More rows exist than one read returns, so what you see here is the first 1000. Nothing is
					missing from the database — the list on this screen is short, and it will stay short until
					paging is added.
				</InfoBanner>
			)}

			<Table<Agent>
				columns={[
					{
						id: "name",
						header: "Agent",
						accessor: (a) => (
							<>
								<div className="font-medium text-content">{a.name}</div>
								{a.description && <div className="text-xs text-muted">{a.description}</div>}
							</>
						),
					},
					{ id: "kind", header: "Kind", accessor: (a) => a.kind },
					{
						id: "status",
						header: "Status",
						accessor: (a) => (
							<Select
								value={a.status}
								disabled={busy}
								onChange={(e) => void run(() => api.updateAgent(a.id, { status: e.target.value }))}
								options={STATUSES}
							/>
						),
					},
					{
						id: "id",
						header: "Id",
						accessor: (a) => <span className="font-mono text-xs text-muted">{a.id}</span>,
					},
					{
						id: "actions",
						header: "",
						accessor: (a) => (
							<Button
								variant="ghost"
								size="sm"
								disabled={busy}
								onClick={() => void run(() => api.deleteAgent(a.id))}
							>
								Delete
							</Button>
						),
					},
				]}
				data={agents.state === "ready" ? agents.data.agents : []}
				keyExtractor={(a) => a.id}
				loading={agents.state === "loading"}
				emptyMessage="No agents yet. Add the first one below."
			/>

			<form className="mt-6 flex flex-wrap items-end gap-3" onSubmit={create}>
				<Input
					label="Name"
					placeholder="head-of-ads"
					value={name}
					onChange={(e) => setName(e.target.value)}
				/>
				<Select
					label="Kind"
					value={kind}
					onChange={(e) => setKind(e.target.value)}
					options={[
						{ value: "buyer", label: "buyer" },
						{ value: "orchestrator", label: "orchestrator" },
						{ value: "analyst", label: "analyst" },
					]}
				/>
				<Button type="submit" loading={busy} disabled={!name.trim()}>
					Add agent
				</Button>
			</form>
		</Page>
	);
}
